// src/pages/ProgramView.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import VideoCard from '../components/VideoCard';
import VideoPlayerModal from '../components/VideoPlayerModal';
import FilterBar from '../components/FilterBar';
import FormattedText from '../components/FormattedText';
import AboutMeModal from '../components/AboutMeModal';
import { fetchPrograms, fetchSiteContent, fetchUserCompletions, markWorkoutComplete, deleteCompletionById, fetchVideoCompletions, indexCompletions, fetchPods, getUserPod, getUserPods, getUserWaitlistPod, getAvailablePods, getUnlockedWeeks, joinPod, joinWaitlist, leaveWaitlist, getPodSaleStatus, generateSlug } from '../services/firebase';
import { fetchVideosFromCollection } from '../services/bunnyStream';
import { fetchProgramVideos, shapeProgramVideo } from '../services/programVideos';
import { joinProgramWaitlist, leaveProgramWaitlist, isUserOnProgramWaitlist } from '../services/programWaitlist';
import { trainerWallClockToUTC, formatViewerLocalDate, formatViewerLocalTime, isViewerLocalToday } from '../utils/timezone';
import MOCK_PROGRAMS from '../data/mockPrograms';

// ── Smart Schedule Card — focal meeting shown, past/future collapsible ──
function ScheduleCard({ focal, past, future, hasPast, hasFuture, renderMeetingRow, supportingMaterialUrl }) {
    const [showPast, setShowPast] = useState(false);
    const [showFuture, setShowFuture] = useState(false);
    const toggleStyle = {
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        background: 'var(--mom-cream)', border: '1px solid var(--mom-cream-dark)',
        borderRadius: 10, cursor: 'pointer', padding: '10px 14px',
        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
        color: 'var(--mom-charcoal)', transition: 'all 0.15s ease',
    };
    return (
        <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '20px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>Meeting Schedule</h3>
                {supportingMaterialUrl && (
                    <a href={supportingMaterialUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-link)', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                            Supporting Materials
                        </span>
                    </a>
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Past meetings — collapsible */}
                {hasPast && (
                    <>
                        <button onClick={() => setShowPast(!showPast)} style={toggleStyle}>
                            <span style={{ transform: showPast ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: 10 }}>▼</span>
                            <span>{showPast ? 'Hide' : 'Show'} {past.length} previous meeting{past.length !== 1 ? 's' : ''}</span>
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: 'var(--mom-warm-gray)' }}>
                                {past.filter((m) => m.recordingUrl).length > 0 ? `${past.filter((m) => m.recordingUrl).length} recording${past.filter((m) => m.recordingUrl).length !== 1 ? 's' : ''} available` : ''}
                            </span>
                        </button>
                        {showPast && past.map(renderMeetingRow)}
                    </>
                )}

                {/* Focal meeting — always visible */}
                {focal && renderMeetingRow(focal)}

                {/* Future meetings — collapsible */}
                {hasFuture && (
                    <>
                        <button onClick={() => setShowFuture(!showFuture)} style={toggleStyle}>
                            <span style={{ transform: showFuture ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: 10 }}>▼</span>
                            <span>{showFuture ? 'Hide' : 'Show'} {future.length} upcoming meeting{future.length !== 1 ? 's' : ''}</span>
                        </button>
                        {showFuture && future.map(renderMeetingRow)}
                    </>
                )}
            </div>
        </div>
    );
}

// ── Collapsible section wrapper ─────────────────────────────
function CollapsibleSection({ title, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', marginBottom: 24, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
            <button onClick={() => setOpen(!open)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 32px', background: 'none', border: 'none', cursor: 'pointer' }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', margin: 0 }}>{title}</h3>
                <span style={{ fontSize: 18, color: 'var(--mom-warm-gray)', transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
            </button>
            <div style={{ maxHeight: open ? '5000px' : '0', overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
                <div style={{ padding: '0 32px 28px' }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

// ── FAQ section ─────────────────────────────────────────────
function FAQSection({ faqs, defaultOpen = false }) {
    const [openIdx, setOpenIdx] = useState(null);
    if (!faqs || faqs.length === 0) return null;
    return (
        <CollapsibleSection title="Frequently Asked Questions" defaultOpen={defaultOpen}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {faqs.map((faq, i) => (
                    <div key={i} style={{ borderBottom: i < faqs.length - 1 ? '1px solid var(--mom-cream-dark)' : 'none' }}>
                        <button onClick={() => setOpenIdx(openIdx === i ? null : i)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12 }}>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-charcoal)', flex: 1 }}>{faq.question}</span>
                            <span style={{ fontSize: 14, color: 'var(--mom-warm-gray)', transition: 'transform 0.2s', transform: openIdx === i ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
                        </button>
                        <div style={{ maxHeight: openIdx === i ? '1000px' : '0', overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                            <div style={{ paddingBottom: 16 }}>
                                <FormattedText text={faq.answer} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', lineHeight: 1.7 }} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </CollapsibleSection>
    );
}

// ── Testimonials section (show first 3, then "see all" modal) ──
function TestimonialsSection({ testimonials, compact = false }) {
    const [showAll, setShowAll] = useState(false);
    if (!testimonials || testimonials.length === 0) return null;
    const preview = testimonials.slice(0, 3);
    const hasMore = testimonials.length > 3;
    return (
        <>
            <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '28px 32px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>What Others Are Saying</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {preview.map((t, i) => (
                        <div key={i} style={{ position: 'relative', padding: compact ? '16px 20px' : '20px 24px', background: 'var(--mom-cream)', borderRadius: compact ? 14 : 16 }}>
                            <div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: compact ? 32 : 36, color: 'var(--mom-primary)', opacity: 0.15, position: 'absolute', top: compact ? 6 : 8, left: compact ? 14 : 16, lineHeight: 1 }}>"</div>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: compact ? 14 : 15, color: 'var(--mom-charcoal)', lineHeight: 1.75, position: 'relative', zIndex: 1 }}>{t.text}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: compact ? 10 : 14 }}>
                                {!compact && (
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700 }}>
                                        {t.name ? t.name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                )}
                                <div>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: compact ? 12 : 13, fontWeight: 600, color: compact ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)' }}>{compact ? `— ${t.name || 'Anonymous'}` : (t.name || 'Anonymous')}{t.detail ? `, ${t.detail}` : ''}</p>
                                    {!compact && t.detail && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)' }}>{t.detail}</p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {hasMore && (
                    <button onClick={() => setShowAll(true)} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-link)', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 0 0', display: 'block' }}>
                        See all testimonials →
                    </button>
                )}
            </div>
            {showAll && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowAll(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(62, 47, 47, 0.45)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }} />
                    <div onClick={(e) => e.stopPropagation()} style={{
                        position: 'relative',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(251,252,253,0.95) 40%, rgba(246,248,250,0.97) 100%)',
                        borderRadius: 28, border: '1px solid rgba(255,255,255,0.6)',
                        boxShadow: '0 32px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.3) inset, 0 1px 3px rgba(255,255,255,0.5) inset',
                        maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40%', background: 'radial-gradient(ellipse 60% 70% at 30% 0%, rgba(255,255,255,0.35) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0, borderRadius: '28px 28px 0 0' }} />
                        <div style={{ padding: '28px 32px 0', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>What Others Are Saying</h2>
                                <button onClick={() => setShowAll(false)} style={{ background: 'rgba(255,255,255,0.7)', border: 'none', fontSize: 18, color: 'var(--mom-warm-gray)', cursor: 'pointer', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>✕</button>
                            </div>
                        </div>
                        <div style={{ overflowY: 'auto', padding: '0 32px 32px', position: 'relative', zIndex: 1 }}>
                            {testimonials.map((t, i) => (
                                <div key={i} style={{ position: 'relative', padding: '20px 24px', background: 'rgba(255,255,255,0.5)', borderRadius: 16, marginBottom: 14, border: '1px solid rgba(255,255,255,0.4)' }}>
                                    <div style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 36, color: 'var(--mom-primary)', opacity: 0.15, position: 'absolute', top: 8, left: 16, lineHeight: 1 }}>"</div>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-charcoal)', lineHeight: 1.75, position: 'relative', zIndex: 1 }}>{t.text}</p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700 }}>{t.name ? t.name.charAt(0).toUpperCase() : '?'}</div>
                                        <div>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{t.name || 'Anonymous'}</p>
                                            {t.detail && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)' }}>{t.detail}</p>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ── About Danielle teaser (opens modal) ─────────────────────
function AboutDanielleTeaser({ siteContent }) {
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const hasAbout = siteContent?.aboutHeadline || siteContent?.aboutIntro || siteContent?.aboutBody;
    if (!hasAbout) return null;
    const photo = siteContent.aboutPhoto;
    const headline = siteContent.aboutHeadline || 'Meet Danielle';
    const tagline = siteContent.aboutTagline || '';
    const intro = siteContent.aboutIntro || '';
    const preview = intro.length > 150 ? intro.substring(0, 150).replace(/\*+/g, '').trim() + '...' : intro.replace(/\*+/g, '');

    return (
        <>
            <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '28px 32px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    {photo ? (
                        <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                            <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                        </div>
                    ) : (
                        <div style={{ width: 64, height: 64, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>D</span>
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 2 }}>{headline}</h3>
                        {tagline && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-primary)', fontWeight: 600, marginBottom: 4 }}>{tagline}</p>}
                        {preview && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', lineHeight: 1.5 }}>{preview}</p>}
                    </div>
                </div>
                <button className="mom-btn mom-btn-ghost" onClick={() => setShowModal(true)} style={{ marginTop: 14, fontSize: 13, color: 'var(--mom-link)', fontWeight: 600, padding: '6px 0' }}>
                    More about Danielle →
                </button>
            </div>
            {showModal && <AboutMeModal siteContent={siteContent} onClose={() => setShowModal(false)} onNavigate={navigate} />}
        </>
    );
}




// ── Sign-in-first purchase intent (2026-10-06) ──────────────────────────────
// A signed-out visitor who taps Purchase/Enroll signs up (or in) first. Her
// choice survives Clerk's modal + any sign-in redirect/remount in
// sessionStorage, and checkout resumes automatically once she's signed in.
// 15-minute TTL: abandoning sign-up never triggers a surprise checkout later.
const PURCHASE_INTENT_KEY = 'mom_program_purchase_intent';
const PURCHASE_INTENT_TTL_MS = 15 * 60 * 1000;
function savePurchaseIntent(programId, podId) {
    try { sessionStorage.setItem(PURCHASE_INTENT_KEY, JSON.stringify({ programId, podId: podId || '', at: Date.now() })); } catch (_) { /* storage unavailable */ }
}
function consumePurchaseIntent(programId) {
    try {
        const raw = sessionStorage.getItem(PURCHASE_INTENT_KEY);
        if (!raw) return null;
        const intent = JSON.parse(raw);
        if (!intent || intent.programId !== programId) return null; // another program's intent is left alone
        sessionStorage.removeItem(PURCHASE_INTENT_KEY);
        return Date.now() - (intent.at || 0) <= PURCHASE_INTENT_TTL_MS ? intent : null;
    } catch (_) { return null; }
}

// "Already enrolled? Log in" (2026-10-07). Members who bought a program and
// later reopen its ORIGINAL public link while signed out land on the sales
// view — and once enrollment closes that view only offers "Join Waitlist", so
// they think they've lost access. Shown to signed-out visitors only, under each
// waitlist CTA. Copy is admin-editable (siteConfig) with inline fallbacks.
// Two looks (owner chose the outlined button from a live-theme mockup,
// docs/ux/program-login-button-preview.html): `button` sits beside a Join
// Waitlist button as an equal-size OUTLINED pink button — findable, but the
// dark Join Waitlist stays the primary action for prospects; `line` is the
// quiet text form, used under the Upcoming Groups list where each group
// already carries its own small Join Waitlist button.
function AlreadyEnrolledLogin({ siteContent, onLogIn, marginTop = 12, variant = 'line', padY = 12, padX = 32 }) {
    const [hover, setHover] = useState(false);
    if (variant === 'button') {
        // Join Waitlist has no border; subtract the 1.5px outline from the
        // padding so both buttons come out exactly the same size.
        return (
            <button type="button" className="mom-btn" onClick={onLogIn}
                onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{
                    padding: `${padY - 1.5}px ${padX - 1.5}px`, borderRadius: 14, fontSize: 15, fontWeight: 600,
                    background: hover ? 'color-mix(in srgb, var(--mom-primary) 8%, transparent)' : 'transparent',
                    color: 'var(--mom-primary)', border: '1.5px solid var(--mom-primary)',
                }}>
                {siteContent?.programAlreadyEnrolledButtonLabel || 'Already enrolled? Log in'}
            </button>
        );
    }
    const prompt = siteContent?.programAlreadyEnrolledText || 'Already enrolled?';
    const link = siteContent?.programAlreadyEnrolledLinkLabel || 'Log in to see your program';
    return (
        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'color-mix(in srgb, var(--mom-charcoal) 70%, var(--mom-cream))', marginTop }}>
            {prompt}{' '}
            <button type="button" onClick={onLogIn}
                onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 700,
                    color: 'var(--mom-primary)', textDecoration: 'underline',
                    textUnderlineOffset: 3, textDecorationThickness: hover ? 2 : 1,
                }}>{link}</button>
        </p>
    );
}

function ProgramView({ demoMode }) {
    const { programId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [program, setProgram] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [videosLoading, setVideosLoading] = useState(false);
    // True only when a video-list load FAILED (transient error) and left nothing
    // to show — drives a Retry card instead of the false "No videos" empty state.
    const [videosError, setVideosError] = useState(false);
    // Bumped by the Retry button to re-run the load effect.
    const [videosReloadKey, setVideosReloadKey] = useState(0);
    const [activeVideo, setActiveVideo] = useState(null);
    const [lengthFilter, setLengthFilter] = useState([]);
    const [groupFilter, setGroupFilter] = useState([]);
    const [sortBy, setSortBy] = useState('newest');
    const [navLabel, setNavLabel] = useState('');
    const [siteContent, setSiteContent] = useState(null);
    const [completedIds, setCompletedIds] = useState(new Set());
    // ORDERING snapshot of completions, taken when the page's videos load and
    // NOT updated by completions made during this visit. Cards, weeks and the
    // list sort completed items down using THIS set, so nothing moves under the
    // member's finger mid-visit. Live ✓ / counts still use `completedIds`.
    // Why: a silent auto-complete re-sorted Workout 1 to the bottom of its week
    // and slid Workout 2 into its slot; members then tapped "Log this workout"
    // there and logged Workout 2 by mistake (39 times, 2026-06-08 → 10-05).
    const [orderCompletedIds, setOrderCompletedIds] = useState(new Set());
    // Per-video completion history for the VideoCard chip (event-log model).
    const [perVideoCompletions, setPerVideoCompletions] = useState({});

    // Stable callback for the modal's program auto-complete (75%). MUST be a hook
    // declared BEFORE any early return (rules-of-hooks) and memoized — the modal's
    // effect resets its one-shot ref when onAutoComplete identity changes, so a
    // per-render function would allow repeat fires. Mirrors Workouts.
    const handleModalAutoComplete = useCallback((videoId) => {
        setCompletedIds((prev) => prev.has(videoId) ? prev : new Set(prev).add(videoId));
        setPerVideoCompletions((prev) => {
            const cur = prev[videoId];
            return { ...prev, [videoId]: { count: (cur?.count || 0) + 1, lastCompletedAt: new Date().toISOString() } };
        });
    }, []);
    const [completedFilter, setCompletedFilter] = useState(null);
    // Cohort state
    const [pods, setPods] = useState([]);
    const [userPods, setUserPods] = useState([]); // all pods user belongs to
    const [userPod, setUserPod] = useState(null); // currently active/viewed pod
    const [userWaitlistPod, setUserWaitlistPod] = useState(null);
    const [selectedPodId, setSelectedPodId] = useState(null);
    const [joiningPod, setJoiningPod] = useState(false);
    const [joiningWaitlist, setJoiningWaitlist] = useState(false);
    // Program-level waitlist state (when no pods are available)
    const [onProgramWaitlist, setOnProgramWaitlist] = useState(false);
    const [joiningProgramWaitlist, setJoiningProgramWaitlist] = useState(false);
    // Stripe / purchase state
    const [checkingPayment, setCheckingPayment] = useState(false);
    const [purchaseProcessing, setPurchaseProcessing] = useState(false);
    const [enrollModalOpen, setEnrollModalOpen] = useState(false);
    // No per-purchase waiver modal. Express consent is captured inline at
    // signup via Clerk's "Require express consent" checkbox; the audit trail
    // is mirrored to Firestore by `api/clerk-webhook.js` on `user.created`.
    // See `docs/decisions/adr-waiver-clerk-express-consent.md`. Existing
    // users' prior program-specific waivers remain in Firestore as legal
    // proof of their original consent.

    const clerkData = useUser();
    const clerk = useClerk();
    let isAdmin = false;
    let isFriend = false;
    let userId = '';
    let purchasedIds = [];
    let userEmail = '';
    let clerkUser = null;
    if (!demoMode) {
        const user = clerkData?.user;
        clerkUser = user;
        isAdmin = user?.publicMetadata?.role === 'admin';
        isFriend = user?.publicMetadata?.role === 'friend';
        purchasedIds = Array.isArray(user?.publicMetadata?.purchasedPrograms) ? user.publicMetadata.purchasedPrograms : [];
        userId = user?.id || '';
        userEmail = user?.primaryEmailAddress?.emailAddress || '';
    } else {
        isAdmin = true; userId = 'demo';
    }

    // Preview mode: admin can see app as a regular user would
    const previewAsUser = typeof window !== 'undefined' && localStorage.getItem('mom-preview-as-user') === 'true';
    if (previewAsUser && isAdmin) { isAdmin = false; }
    const hasFullAccess = isAdmin || isFriend;

    // Determine purchase status once program is resolved
    const isPurchased = demoMode || hasFullAccess || (program && purchasedIds.includes(program.id));

    // Gate inactive programs at render time (not in useEffect) so admin role is always current
    const isInactiveBlocked = program && program.active === false && !isAdmin;

    useEffect(() => {
        async function load() {
            setLoading(true);
            const [programs, content] = await Promise.all([fetchPrograms(), fetchSiteContent()]);
            if (content && content.dashboardNavLabel) setNavLabel(content.dashboardNavLabel);
            setSiteContent(content);
            const allProgs = programs || MOCK_PROGRAMS;
            const found = allProgs.find((p) => p.id === programId || generateSlug(p.name) === programId);
            setProgram(found || null);
            setLoading(false);

            if (found) {
                const realId = found.id;

                if (found.programType === 'cohort') {
                    const allPods = await fetchPods(realId);
                    setPods(allPods);
                    const allUserPods = getUserPods(allPods, userId);
                    setUserPods(allUserPods);
                    // Select the best active pod: prefer active/upcoming over completed, most recent first
                    const activePod = allUserPods.find((p) => p.status === 'active')
                        || allUserPods.find((p) => p.status === 'upcoming')
                        || allUserPods[allUserPods.length - 1]
                        || null;
                    setUserPod(activePod);
                    if (!activePod) setUserWaitlistPod(getUserWaitlistPod(allPods, userId));
                    // Check program-level waitlist (for when no pods are available)
                    if (userId) {
                        isUserOnProgramWaitlist(userId, realId).then((on) => setOnProgramWaitlist(on));
                    }
                }
            }
        }
        load();
    }, [programId]);


    // Post-payment polling: when redirected back from Stripe with ?session_id=xxx
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (!sessionId || demoMode || !program) return;
        const returnPodId = searchParams.get('pod');

        // If already purchased (webhook was fast), skip polling but still join pod
        if (isPurchased) {
            (async () => {
                if (returnPodId && program.programType === 'cohort') {
                    setCheckingPayment(true);
                    try {
                        const ok = await joinPod(returnPodId, userId);
                        if (ok) {
                            const allPods = await fetchPods(program.id);
                            setPods(allPods);
                            const allUserPods = getUserPods(allPods, userId);
                            setUserPods(allUserPods);
                            // Select the newly joined pod as active
                            const joinedPod = allUserPods.find((p) => p.id === returnPodId);
                            setUserPod(joinedPod || allUserPods[allUserPods.length - 1] || null);
                        }
                    } catch (e) { console.error('Auto-join pod failed:', e); }
                    setCheckingPayment(false);
                }
                setSearchParams({}, { replace: true });
            })();
            return;
        }

        setCheckingPayment(true);
        let attempts = 0;
        const maxAttempts = 20; // ~40 seconds max
        const poll = setInterval(async () => {
            attempts++;
            try {
                // Reload Clerk user to get fresh publicMetadata
                if (clerkUser) await clerkUser.reload();
                const freshPrograms = clerkUser?.publicMetadata?.purchasedPrograms || [];
                if (freshPrograms.includes(program.id)) {
                    clearInterval(poll);
                    // Auto-join pod for cohort programs (podId survives redirect via URL param)
                    if (returnPodId && program.programType === 'cohort') {
                        try {
                            const ok = await joinPod(returnPodId, userId);
                            if (ok) {
                                const allPods = await fetchPods(program.id);
                                setPods(allPods);
                                const allUserPods = getUserPods(allPods, userId);
                                setUserPods(allUserPods);
                                const joinedPod = allUserPods.find((p) => p.id === returnPodId);
                                setUserPod(joinedPod || allUserPods[allUserPods.length - 1] || null);
                            }
                        } catch (e) { console.error('Auto-join pod failed:', e); }
                    }
                    setCheckingPayment(false);
                    setSearchParams({}, { replace: true });
                }
            } catch (e) { /* ignore reload errors */ }
            if (attempts >= maxAttempts) {
                clearInterval(poll);
                setCheckingPayment(false);
            }
        }, 2000);
        return () => clearInterval(poll);
    }, [searchParams, program, demoMode, clerkUser]);

    // Handle cancelled checkout
    useEffect(() => {
        if (searchParams.get('cancelled') === 'true') {
            setSearchParams({}, { replace: true });
        }
    }, [searchParams]);

    // Load videos once program is resolved and purchase confirmed.
    // Sources: Bunny collection (if set) + in-app recorded videos (programVideos).
    // The Bunny-only gate was relaxed so a program can be recorded-videos-only or
    // mix both — see docs/decisions/adr-program-recorded-videos.md.
    useEffect(() => {
        if (!program || !isPurchased) return;
        let cancelled = false;
        async function loadVideos() {
            setVideosLoading(true);
            setVideosError(false);
            const [vids, recorded, completions] = await Promise.all([
                program.bunnyCollectionId ? fetchVideosFromCollection(program.bunnyCollectionId) : Promise.resolve([]),
                fetchProgramVideos(program.id),
                fetchUserCompletions(userId),
            ]);
            if (cancelled) return;
            // Bunny videos — apply admin-authored per-video description overrides
            // (program doc, keyed by Bunny videoId). Absent/blank ⇒ keep the
            // auto-generated buildVideo() description — byte-identical to before.
            const descOverrides = program.videoDescriptions || {};
            const bunnyVideos = (vids || []).map((v) => (
                descOverrides[v.id] && descOverrides[v.id].trim()
                    ? { ...v, description: descOverrides[v.id] }
                    : v
            ));
            // In-app recorded videos (Mux) — READY only — shaped like a class
            // recording so VideoPlayerModal plays them via mux-player. Their stable
            // id is the programVideos doc id; videoOrder[] interleaves them with
            // Bunny ids (distinct namespaces, so mixing is safe).
            const recordedVideos = (recorded || [])
                .filter((pv) => pv.status === 'ready' && pv.muxPlaybackId)
                .map(shapeProgramVideo);
            // Distinguish a transient load FAILURE from a genuinely empty program.
            // The data sources return null ONLY on error (a successful-but-empty
            // collection returns []). When a source we expected to carry content
            // errored AND we're left with nothing to show, surface a Retry card
            // instead of the false "No videos uploaded yet" state — the root of the
            // intermittent-empty-program reports. Gating on "got nothing" guarantees
            // we NEVER convert a currently-working (even partial) render into an
            // error, so there's zero regression for the happy path.
            const bunnyFailed = !!program.bunnyCollectionId && vids === null;
            const recordedFailed = recorded === null;
            if ((bunnyFailed || recordedFailed) && bunnyVideos.length === 0 && recordedVideos.length === 0) {
                setVideosError(true);
                setVideosLoading(false);
                return;
            }
            setVideos([...bunnyVideos, ...recordedVideos]);
            // Only count completions for videos currently assigned to the program
            const assignedIds = new Set();
            if (program.programType === 'cohort' && program.weeks) {
                program.weeks.forEach((w) => (w.videoIds || []).forEach((id) => assignedIds.add(id)));
            } else if (program.videoOrder?.length > 0) {
                program.videoOrder.forEach((id) => assignedIds.add(id));
            } else {
                bunnyVideos.forEach((v) => assignedIds.add(v.id));
            }
            // Recorded videos are program-owned by construction, so they always
            // count toward THIS program regardless of videoOrder state.
            recordedVideos.forEach((v) => assignedIds.add(v.id));
            const { completedIds: ids, perVideo } = indexCompletions(
                (completions || []).filter((c) => c.programId === program.id && assignedIds.has(c.videoId))
            );
            setCompletedIds(ids);
            setOrderCompletedIds(ids);
            setPerVideoCompletions(perVideo);
            setVideosLoading(false);
        }
        loadVideos();
        return () => { cancelled = true; };
    }, [program, isPurchased, videosReloadKey]);

    // Resume a purchase started while signed out, once she's signed in.
    // proceedToCheckout is declared BELOW the early returns, so it's reached
    // through a ref that's only set once a render gets that far — never a
    // use-before-declaration. The intent is consumed only when checkout can
    // actually resume. Never re-charges: skips if she already owns a
    // self-paced program, or is already in the pod she picked.
    const proceedToCheckoutRef = useRef(null);
    const purchaseInFlightRef = useRef(false);

    // "Already enrolled? Log in" — this page loads the member's pod ONCE, keyed
    // on the program, so signing in from Clerk's modal without a reload would
    // show the purchased view with NO group ("you'll be added to a group soon").
    // When the sign-in started from our link, reload the page once she is signed
    // in so everything loads as a fresh visit. forceRedirectUrl covers sign-in
    // methods that leave the page (Google) and come back here.
    const loginIntentRef = useRef(false);
    const handleAlreadyEnrolledLogin = () => {
        loginIntentRef.current = true;
        const back = window.location.pathname + window.location.search;
        try {
            clerk.openSignIn({ forceRedirectUrl: back, signUpForceRedirectUrl: back });
        } catch (e) {
            console.error('Could not open sign-in:', e);
        }
    };
    useEffect(() => {
        if (userId && loginIntentRef.current) {
            loginIntentRef.current = false;
            window.location.reload();
        }
    }, [userId]);
    // Back from Stripe via the browser's back button can restore this page from
    // the back/forward cache with "Redirecting…" still set — reset it.
    useEffect(() => {
        const onShow = (e) => { if (e.persisted) { purchaseInFlightRef.current = false; setPurchaseProcessing(false); } };
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, []);
    useEffect(() => {
        if (loading || demoMode || !userId || !program || !proceedToCheckoutRef.current) return;
        const intent = consumePurchaseIntent(program.id);
        if (!intent) return;
        const owns = purchasedIds.includes(program.id);
        if (owns && (!intent.podId || userPods.some((p) => p.id === intent.podId))) return;
        proceedToCheckoutRef.current(intent.podId || undefined);
    });

    if (loading) {
        return (
            <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
                <AppNav demoMode={demoMode} />
                <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px', filter: 'blur(4px)', opacity: 0.5 }}>
                    <div className="mom-skeleton" style={{ height: 200, borderRadius: 24, marginBottom: 24 }} />
                    <div className="mom-skeleton" style={{ height: 48, borderRadius: 14, marginBottom: 16, width: '60%' }} />
                    <div className="mom-skeleton" style={{ height: 16, borderRadius: 8, marginBottom: 8, width: '90%' }} />
                    <div className="mom-skeleton" style={{ height: 16, borderRadius: 8, marginBottom: 24, width: '70%' }} />
                    <div className="mom-skeleton" style={{ height: 320, borderRadius: 20 }} />
                </div>
            </div>
        );
    }

    if (!program || isInactiveBlocked) {
        return (
            <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
                <AppNav demoMode={demoMode} />
                <div style={{ textAlign: 'center', padding: '100px 24px' }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 18, color: 'var(--mom-warm-gray)' }}>Program not found.</p>
                    <button className="mom-btn mom-btn-primary" onClick={() => navigate('/dashboard')} style={{ marginTop: 24 }}>Back to {navLabel}</button>
                </div>
            </div>
        );
    }

    // Additive manual completion (event-log model), scoped to THIS program.
    const handleLogComplete = async (video) => {
        const occurrenceKey = `${video.id}_${program.id}_manual_${Date.now()}`;
        const rec = await markWorkoutComplete(userId, video.id, video.title, program.id, { occurrenceKey, source: 'manual' });
        if (rec) {
            setCompletedIds((prev) => new Set(prev).add(video.id));
            setPerVideoCompletions((prev) => {
                const cur = prev[video.id];
                const last = rec.completedAt || new Date().toISOString();
                return { ...prev, [video.id]: { count: (cur?.count || 0) + 1, lastCompletedAt: last } };
            });
        }
        return rec;
    };

    const handleUndoComplete = async (video, completionId) => {
        const ok = await deleteCompletionById(userId, completionId);
        if (!ok) return false;
        const remaining = (perVideoCompletions[video.id]?.count || 1) - 1;
        setPerVideoCompletions((prev) => {
            const cur = prev[video.id];
            if (!cur) return prev;
            const count = Math.max(0, (cur.count || 1) - 1);
            const next = { ...prev };
            if (count === 0) { delete next[video.id]; } else { next[video.id] = { ...cur, count }; }
            return next;
        });
        if (remaining <= 0) {
            setCompletedIds((prev) => { const n = new Set(prev); n.delete(video.id); return n; });
        }
        return true;
    };

    // Lazy loader for the "Your history" remover (card + modal). Resolves the
    // id-backed session list just-in-time, scoped to THIS program so a video
    // shared across programs only un-logs its credit here.
    const handleLoadHistory = (video) => {
        if (!userId || !video?.id) return Promise.resolve([]);
        return fetchVideoCompletions(userId, video.id, program.id);
    };

    // Admin-editable completion copy; undefined keys fall back to VideoCard defaults.
    const completionCopy = {
        logLabel: siteContent?.completionLogLabel,
        logAgainLabel: siteContent?.completionLogAgainLabel,
        loggedLabel: siteContent?.completionLoggedLabel,
        undoLabel: siteContent?.completionUndoLabel,
        removeConfirmLabel: siteContent?.completionRemoveConfirmLabel,
        removeKeepLabel: siteContent?.completionRemoveKeepLabel,
        historyDisclosureLabel: siteContent?.completionHistoryDisclosureLabel,
        historyRemoveLabel: siteContent?.completionHistoryRemoveLabel,
    };

    const handleJoinPod = async () => {
        if (!selectedPodId) return;
        setJoiningPod(true);
        const ok = await joinPod(selectedPodId, userId);
        if (ok) {
            const allPods = await fetchPods(program.id);
            setPods(allPods);
            const allUserPods = getUserPods(allPods, userId);
            setUserPods(allUserPods);
            const joinedPod = allUserPods.find((p) => p.id === selectedPodId);
            setUserPod(joinedPod || allUserPods[allUserPods.length - 1] || null);
        }
        setJoiningPod(false);
    };

    const proceedToCheckout = async (overridePodId) => {
        if (purchaseInFlightRef.current) return; // one checkout at a time
        purchaseInFlightRef.current = true;
        setPurchaseProcessing(true);
        try {
            const res = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    programId: program.id,
                    programName: program.name,
                    programSlug: programId,
                    price: program.price,
                    stripeProductId: program.stripeProductId || '',
                    userId: userId || '',
                    userEmail: userEmail || '',
                    podId: overridePodId || selectedPodId || '',
                    isGuest: !userId,
                }),
            });
            const data = await res.json();
            if (data.sessionUrl) {
                window.location.href = data.sessionUrl;
            } else {
                console.error('Checkout error:', data.error);
                purchaseInFlightRef.current = false;
                setPurchaseProcessing(false);
            }
        } catch (err) {
            console.error('Purchase error:', err);
            purchaseInFlightRef.current = false;
            setPurchaseProcessing(false);
        }
    };
    proceedToCheckoutRef.current = proceedToCheckout;

    // Initiate purchase. Express consent is captured at signup via Clerk's
    // express-consent checkbox (see `adr-waiver-clerk-express-consent.md`).
    //
    // SIGN IN FIRST (2026-10-06): a signed-out visitor creates her account (or
    // signs in) BEFORE paying, so the purchase carries her account id and goes
    // straight to the right member. Guest checkout only reached a member if she
    // finished /welcome right away with the same email — every lost purchase
    // in the 5 to 20 October cohort audit was that path. The chosen program +
    // pod are kept in sessionStorage (survives Clerk's sign-in remount/redirect)
    // and checkout continues automatically once she's signed in.
    const handlePurchase = (overridePodId) => {
        if (!program) return;
        setEnrollModalOpen(false);
        if (!userId && !demoMode) {
            savePurchaseIntent(program.id, overridePodId || selectedPodId || '');
            const back = window.location.pathname + window.location.search;
            try {
                clerk.openSignUp({ forceRedirectUrl: back, signInForceRedirectUrl: back });
            } catch (e) {
                console.error('Could not open sign-up:', e);
            }
            return;
        }
        proceedToCheckout(overridePodId);
    };

    const isCohort = program.programType === 'cohort';
    const isSelfPaced = !isCohort;

    // Determine which videos to show
    let visibleVideos = videos;
    let unlockedWeeks = [];

    if (isCohort && userPod && program.weeks) {
        unlockedWeeks = getUnlockedWeeks(program, userPod);
        const unlockedVideoIds = new Set(unlockedWeeks.flatMap((w) => w.videoIds || []));
        // After all weeks unlocked or admin, show all
        const allWeeksUnlocked = unlockedWeeks.length === (program.weeks || []).length;
        if (!allWeeksUnlocked && !hasFullAccess) {
            visibleVideos = videos.filter((v) => unlockedVideoIds.has(v.id));
        }
    }

    if (isSelfPaced && program.videoOrder && program.videoOrder.length > 0) {
        const ordered = [];
        program.videoOrder.forEach((id) => { const v = videos.find((vid) => vid.id === id); if (v) ordered.push(v); });
        videos.forEach((v) => { if (!program.videoOrder.includes(v.id)) ordered.push(v); });
        visibleVideos = ordered;
    }

    // Filter & sort
    const filtered = visibleVideos
        .filter((v) => lengthFilter.length === 0 || lengthFilter.includes(v.length))
        .filter((v) => groupFilter.length === 0 || groupFilter.some((g) => v.groups.includes(g)))
        .filter((v) => { if (completedFilter === 'completed') return completedIds.has(v.id); return true; })
        .sort((a, b) => {
            // For self-paced with order, preserve order
            if (isSelfPaced && program.videoOrder?.length > 0 && sortBy === 'newest') return 0;
            if (!completedFilter) {
                const aComp = orderCompletedIds.has(a.id) ? 1 : 0;
                const bComp = orderCompletedIds.has(b.id) ? 1 : 0;
                if (aComp !== bComp) return aComp - bComp;
            }
            if (sortBy === 'newest') return (b.createdTime || '').localeCompare(a.createdTime || '');
            if (sortBy === 'length-asc') return a.length - b.length;
            if (sortBy === 'length-desc') return b.length - a.length;
            return a.title.localeCompare(b.title);
        });

    const availablePods = getAvailablePods(pods);
    // Pods available for re-enrollment (user is NOT already a participant)
    const userPodIds = new Set(userPods.map((p) => p.id));
    const enrollablePods = availablePods.filter((p) => !userPodIds.has(p.id));

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
                <button className="mom-btn mom-btn-ghost" onClick={() => navigate('/programs')} style={{ color: 'var(--mom-link)', fontSize: 14, marginBottom: 24, padding: '4px 0' }}>
                    ← Back to Programs
                </button>

                {/* ── PROGRAM HEADER ────────────────────────── */}
                {isPurchased ? (
                    /* ── SLIM HEADER FOR PURCHASED USERS ────────── */
                    <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', marginBottom: 24, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                        <div style={{ padding: '18px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {/* Program thumbnail */}
                                {program.imageUrl ? (
                                    <div style={{ width: 44, height: 44, borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
                                        <img src={program.imageUrl} alt={program.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                                    </div>
                                ) : (
                                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>{program.name?.charAt(0) || 'P'}</span>
                                    </div>
                                )}
                                {/* Name + meta */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(17px, 3vw, 24px)', fontStyle: 'italic', color: 'var(--mom-charcoal)', lineHeight: 1.2 }}>{program.name}</h1>
                                    {isCohort && userPod && (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginTop: 2 }}>
                                            {userPod.podName} · Week {unlockedWeeks.length > 0 ? `${unlockedWeeks.length} of ${(program.weeks || []).length}` : 'Not started'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Pod Switcher — only shows when user is in multiple pods */}
                            {isCohort && userPods.length > 1 && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 14, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: 2 }}>
                                    {userPods.map((pod) => {
                                        const isActive = userPod?.id === pod.id;
                                        const isCompleted = pod.status === 'completed';
                                        return (
                                            <button key={pod.id} className="mom-btn" onClick={() => setUserPod(pod)}
                                                style={{
                                                    padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                                    whiteSpace: 'nowrap', flexShrink: 0, border: 'none',
                                                    background: isActive ? 'var(--mom-charcoal)' : 'var(--mom-cream)',
                                                    color: isActive ? 'var(--mom-white)' : isCompleted ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)',
                                                    transition: 'all 0.2s',
                                                }}>
                                                {pod.podName}{isCompleted && !isActive ? ' ✓' : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Community button — own row, full width on mobile */}
                            {program.facebookGroupUrl && (
                                <div style={{ marginTop: 12 }}>
                                    <a href={program.facebookGroupUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                        <button className="mom-btn" style={{ padding: '10px 20px', borderRadius: 50, fontSize: 13, fontWeight: 600, background: '#1877F2', color: 'var(--mom-white)', border: 'none', width: '100%' }}>
                                            Community Group
                                        </button>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* ── FULL HERO FOR NON-PURCHASED ────────────── */
                    <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', marginBottom: 32, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                        {program.imageUrl && (
                            <div className="mom-program-header-img" style={{ width: '100%', background: 'var(--mom-cream)', overflow: 'hidden', position: 'relative' }}>
                                <img src={program.imageUrl} alt={program.name} style={{ width: '100%', height: 'auto', display: 'block' }} referrerPolicy="no-referrer" />
                            </div>
                        )}
                        <div className="mom-program-header-pad" style={{ padding: '28px 40px 32px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(26px, 4vw, 36px)', fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>{program.name}</h1>
                                {isCohort && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, padding: '3px 10px', borderRadius: 10, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', fontWeight: 600 }}>COHORT</span>}
                            </div>
                            <FormattedText text={program.description} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)' }} />

                            {!checkingPayment && (() => {
                                const purchasable = isCohort ? availablePods.filter((p) => { const s = getPodSaleStatus(p); return s === 'immediate' || s === 'on-sale'; }) : [];
                                const onlyWaitlist = isCohort && availablePods.length > 0 && purchasable.length === 0;
                                const noPods = isCohort && availablePods.length === 0;
                                if (onlyWaitlist) return null;
                                if (noPods) {
                                    return (
                                        <div style={{ marginTop: 20 }}>
                                            {onProgramWaitlist ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-primary)' }}>You're on the waitlist ✓</span>
                                                    <button className="mom-btn mom-btn-ghost" onClick={async () => { await leaveProgramWaitlist(userId, program.id); setOnProgramWaitlist(false); }} style={{ fontSize: 12, color: 'var(--mom-warm-gray)' }}>Leave waitlist</button>
                                                </div>
                                            ) : (
                                                <div className={!userId && !demoMode ? 'mom-waitlist-actions' : undefined}>
                                                <button className="mom-btn" onClick={async () => { if (!userId) { clerk.openSignIn(); return; } setJoiningProgramWaitlist(true); await joinProgramWaitlist(userId, userEmail, program.id, program.name); setOnProgramWaitlist(true); setJoiningProgramWaitlist(false); }} disabled={joiningProgramWaitlist} style={{ padding: '12px 32px', borderRadius: 14, fontSize: 15, fontWeight: 600, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none', opacity: joiningProgramWaitlist ? 0.5 : 1 }}>
                                                    {joiningProgramWaitlist ? 'Joining...' : 'Join Waitlist'}
                                                </button>
                                                {!userId && !demoMode && <AlreadyEnrolledLogin variant="button" siteContent={siteContent} onLogIn={handleAlreadyEnrolledLogin} />}
                                                </div>
                                            )}
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginTop: 8 }}>No groups are currently open. Join the waitlist and we'll let you know when the next round starts.</p>
                                        </div>
                                    );
                                }
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
                                        <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--mom-primary)' }}>{`$${program.price} USD`}</span>
                                        {isCohort ? (
                                            <button className="mom-btn mom-btn-primary" onClick={() => setEnrollModalOpen(true)} style={{ padding: '12px 32px' }}>
                                                Enroll Now
                                            </button>
                                        ) : (
                                            <button className="mom-btn mom-btn-primary" onClick={() => handlePurchase()} disabled={purchaseProcessing} style={{ padding: '12px 32px', opacity: purchaseProcessing ? 0.7 : 1 }}>
                                                {purchaseProcessing ? 'Redirecting...' : 'Purchase Program'}
                                            </button>
                                        )}
                                        {!userId && !demoMode && (<p style={{ flexBasis: '100%', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, lineHeight: 1.5, color: 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))', margin: '0' }}>{siteContent?.programSignInFirstNote || "You'll create your free account (or sign in) first — then pay. It keeps your purchase safely tied to you."}</p>)}
                                    </div>
                                );
                            })()}

                            {checkingPayment && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, padding: '16px 24px', background: 'color-mix(in srgb, var(--mom-primary) 6%, transparent)', borderRadius: 14 }}>
                                    <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid var(--mom-cream-dark)", borderTopColor: "var(--mom-primary)", animation: "spin 0.8s linear infinite" }} />
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-charcoal)', fontWeight: 500 }}>Confirming your purchase...</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isPurchased ? (
                    <>
                        {/* ═══ PURCHASED VIEW ═══ */}
                        {/* 1. Schedule (if meetings exist) */}
                        {isCohort && userPod && userPod.meetings && userPod.meetings.length > 0 && (() => {
                            const now = new Date();
                            const meetings = userPod.meetings.map((m, i) => {
                                // Meeting time is stored as Danielle-local wall clock (CT).
                                // Convert to a true UTC moment so we can compare against `now`
                                // accurately and render in the viewer's local tz.
                                const meetStart = m.date ? trainerWallClockToUTC(m.date, m.time) : null;
                                // Meeting-day fallback for entries with no time set: treat as
                                // end-of-day Chicago to match legacy "any time today" behavior.
                                const meetDayAnchor = m.date && !m.time ? trainerWallClockToUTC(m.date, '23:59') : null;
                                const meetMoment = meetStart || meetDayAnchor;
                                const isToday = isViewerLocalToday(meetMoment, now);
                                let meetingEnded = false;
                                if (meetStart) {
                                    meetingEnded = now.getTime() > meetStart.getTime() + 60 * 60 * 1000;
                                } else if (meetDayAnchor) {
                                    meetingEnded = now.getTime() > meetDayAnchor.getTime();
                                }
                                const isFuture = meetMoment && meetMoment.getTime() > now.getTime() && !isToday;
                                return { ...m, idx: i, meetDate: meetMoment, isToday, meetingEnded, isFuture };
                            });
                            // Find the "focal" meeting: today's, or next upcoming, or last completed
                            const todayMeeting = meetings.find((m) => m.isToday);
                            const nextUpcoming = meetings.find((m) => !m.meetingEnded && !m.isToday);
                            const focal = todayMeeting || nextUpcoming || meetings[meetings.length - 1];
                            const focalIdx = focal ? focal.idx : 0;
                            const past = meetings.filter((m) => m.meetingEnded && m.idx !== focalIdx);
                            const future = meetings.filter((m) => m.isFuture && m.idx !== focalIdx);
                            const hasPast = past.length > 0;
                            const hasFuture = future.length > 0;

                            const renderMeetingRow = (m) => (
                                <div key={m.idx} className="mom-meeting-item" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: m.isToday ? 'color-mix(in srgb, var(--mom-primary) 6%, transparent)' : 'var(--mom-cream)', borderRadius: 12, border: m.isToday ? '1.5px solid var(--mom-primary)' : '1px solid transparent', opacity: m.meetingEnded && !m.isToday ? 0.7 : 1, flexWrap: 'wrap' }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: m.isToday ? 'var(--mom-primary)' : m.meetingEnded ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)', color: 'var(--mom-white)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{m.idx + 1}</div>
                                    <div style={{ flex: 1, minWidth: 120 }}>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{m.title || `Week ${m.idx + 1} Meeting`}</p>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                                            {m.meetDate ? formatViewerLocalDate(m.meetDate) : 'Date TBD'}
                                            {m.time && m.meetDate ? ` at ${formatViewerLocalTime(m.meetDate)}` : ''}
                                        </p>
                                    </div>
                                    {m.zoomLink && !m.meetingEnded && (
                                        <a href={m.zoomLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                            <button className="mom-btn" style={{ padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 700, background: m.isToday ? 'var(--mom-primary)' : 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none' }}>
                                                {m.isToday ? 'Join Now' : 'Zoom Link'}
                                            </button>
                                        </a>
                                    )}
                                    {m.meetingEnded && m.recordingUrl && (
                                        <a href={m.recordingUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-link)', textDecoration: 'none' }}>
                                            View Recording →
                                        </a>
                                    )}
                                    {m.meetingEnded && !m.recordingUrl && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)' }}>Completed</span>}
                                </div>
                            );

                            return (
                                <ScheduleCard
                                    focal={focal}
                                    past={past}
                                    future={future}
                                    hasPast={hasPast}
                                    hasFuture={hasFuture}
                                    renderMeetingRow={renderMeetingRow}
                                    supportingMaterialUrl={userPod.supportingMaterialUrl}
                                />
                            );
                        })()}

                        {/* Cohort: countdown if not started */}
                        {isCohort && userPod && new Date(userPod.startDate) > new Date() && (
                            <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '40px 32px', marginBottom: 24, textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
                                <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>Your program starts soon</p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-primary)', fontWeight: 600 }}>
                                    {new Date(userPod.startDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                </p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginTop: 8 }}>Content will unlock on the start date. Check the meeting schedule above!</p>
                            </div>
                        )}

                        {/* 2. Workouts */}
                        <div style={{ marginBottom: 32 }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 20 }}>Workouts</h2>
                            {isCohort && !userPod && !hasFullAccess && (
                                <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '48px 32px', textAlign: 'center', boxShadow: 'var(--shadow-md)', marginBottom: 20 }}>
                                    <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>You'll be added to a group soon!</p>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7, maxWidth: 480, margin: '0 auto' }}>Your program content and meeting schedule will appear here once you've been assigned to a group. Hang tight!</p>
                                </div>
                            )}
                            {videosError && !videosLoading && !(isCohort && !userPod && !hasFullAccess) ? (
                                <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '40px 32px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)', marginBottom: 16 }}>{siteContent?.programVideosErrorText || "We couldn't load your workouts just now. Please try again."}</p>
                                    <button className="mom-btn mom-btn-primary" onClick={() => setVideosReloadKey((k) => k + 1)} style={{ padding: '10px 28px' }}>{siteContent?.programVideosRetryLabel || 'Retry'}</button>
                                </div>
                            ) : isCohort && (userPod || hasFullAccess) && program.weeks && program.weeks.length > 0 && !videosLoading ? (
                                <div>{(() => {
                                    const weeksWithStatus = (program.weeks || []).map((week) => {
                                        const isUnlocked = unlockedWeeks.some((uw) => uw.weekNumber === week.weekNumber) || hasFullAccess;
                                        const weekVids = (week.videoIds || []).map((vid) => videos.find((v) => v.id === vid)).filter(Boolean);
                                        const doneCount = weekVids.filter((v) => completedIds.has(v.id)).length;
                                        const allDone = weekVids.length > 0 && doneCount === weekVids.length;
                                        // Week placement uses the load-time snapshot (see orderCompletedIds).
                                        const orderAllDone = weekVids.length > 0 && weekVids.every((v) => orderCompletedIds.has(v.id));
                                        return { ...week, isUnlocked, weekVids, doneCount, allDone, orderAllDone };
                                    });
                                    const highestUnlockedWeek = Math.max(...weeksWithStatus.filter((w) => w.isUnlocked).map((w) => w.weekNumber), 0);
                                    weeksWithStatus.forEach((week) => { if (!week.isUnlocked) { week.priority = 1; } else if (week.orderAllDone && week.weekNumber < highestUnlockedWeek) { week.priority = 2; } else { week.priority = 0; } });
                                    const sorted = [...weeksWithStatus].sort((a, b) => { if (a.priority !== b.priority) return a.priority - b.priority; return a.weekNumber - b.weekNumber; });
                                    let lastPriority = -1;
                                    return sorted.map((week) => {
                                        const showLabel = week.priority !== lastPriority;
                                        lastPriority = week.priority;
                                        const sectionLabel = week.priority === 0 ? 'Current' : week.priority === 1 ? 'Upcoming' : 'Completed';
                                        const weekVideos = [...week.weekVids].sort((a, b) => { const aC = orderCompletedIds.has(a.id) ? 1 : 0; const bC = orderCompletedIds.has(b.id) ? 1 : 0; if (aC !== bC) return aC - bC; return 0; });
                                        return (
                                            <div key={week.weekNumber}>
                                                {showLabel && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, color: week.priority === 0 ? 'var(--mom-primary)' : 'var(--mom-warm-gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, marginTop: week.priority > 0 ? 20 : 0 }}>{sectionLabel}</p>}
                                                <div style={{ marginBottom: 24 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                        <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: week.isUnlocked ? 'var(--mom-charcoal)' : 'var(--mom-warm-gray)' }}>{week.title || `Week ${week.weekNumber}`}</h3>
                                                        {week.isUnlocked && weekVideos.length > 0 && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, padding: '3px 12px', borderRadius: 10, background: week.allDone ? 'var(--mom-success)' : 'var(--mom-cream)', color: week.allDone ? 'var(--mom-white)' : 'var(--mom-warm-gray)', fontWeight: 600 }}>{week.allDone ? '✓ Complete' : `${week.doneCount} of ${weekVideos.length} done`}</span>}
                                                        {!week.isUnlocked && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, padding: '3px 10px', borderRadius: 10, background: 'var(--mom-cream-dark)', color: 'var(--mom-warm-gray)' }}>Locked</span>}
                                                    </div>
                                                    {week.description && <FormattedText text={week.description} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 12 }} />}
                                                    {week.isUnlocked ? (weekVideos.length > 0 ? (
                                                        <div className="mom-video-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>{weekVideos.map((v) => <VideoCard key={v.id} video={v} onClick={setActiveVideo} isCompleted={completedIds.has(v.id)} completionCount={perVideoCompletions[v.id]?.count || 0} lastCompletedAt={perVideoCompletions[v.id]?.lastCompletedAt || null} onLogComplete={handleLogComplete} onUndoComplete={handleUndoComplete} onLoadHistory={handleLoadHistory} completionCopy={completionCopy} />)}</div>
                                                    ) : <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', padding: '12px 0' }}>No videos assigned to this week yet.</p>
                                                    ) : (
                                                        <div style={{ background: 'var(--mom-white)', borderRadius: 14, padding: '24px', textAlign: 'center', border: '1px solid var(--mom-cream-dark)' }}>
                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)' }}>This content unlocks on day {week.releaseDayOffset || 0} of your program.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}</div>
                            ) : videosLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px' }}>
                                    <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid var(--mom-cream-dark)", borderTopColor: "var(--mom-primary)", animation: "spin 0.8s linear infinite" }} />
                                </div>
                            ) : isSelfPaced && visibleVideos.length > 0 ? (
                                <>
                                    <div className="mom-video-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                                        {filtered.map((v, i) => <VideoCard key={v.id} video={v} onClick={setActiveVideo} animationDelay={Math.min(i * 0.05, 0.5)} isCompleted={completedIds.has(v.id)} completionCount={perVideoCompletions[v.id]?.count || 0} lastCompletedAt={perVideoCompletions[v.id]?.lastCompletedAt || null} onLogComplete={handleLogComplete} onUndoComplete={handleUndoComplete} onLoadHistory={handleLoadHistory} completionCopy={completionCopy} />)}
                                    </div>
                                    {filtered.length === 0 && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)', textAlign: 'center', padding: 40 }}>No workouts match your filters.</p>}
                                </>
                            ) : (
                                <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '40px 32px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)' }}>No videos uploaded yet. Check back soon!</p>
                                    {isAdmin && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-primary)', marginTop: 8 }}>Admin: Add videos to this program's Bunny Stream collection.</p>}
                                </div>
                            )}
                        </div>

                        {/* 3. Re-enrollment CTA — right after workouts */}
                        {isCohort && isPurchased && enrollablePods.length > 0 && userPods.length > 0 && (() => {
                            const purchasableEnrollable = enrollablePods.filter((p) => { const s = getPodSaleStatus(p); return s === 'immediate' || s === 'on-sale'; });
                            const waitlistEnrollable = enrollablePods.filter((p) => getPodSaleStatus(p) === 'waitlist');
                            if (purchasableEnrollable.length === 0 && waitlistEnrollable.length === 0) return null;
                            return (
                                <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '28px 32px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                                    <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6 }}>Join Another Round</h3>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 16, lineHeight: 1.6 }}>
                                        Ready to keep the momentum going? New groups are forming — jump back in for another round of coaching, calls, and accountability.
                                    </p>
                                    {purchasableEnrollable.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontWeight: 700, color: 'var(--mom-primary)' }}>{`$${program.price} USD`}</span>
                                            <button className="mom-btn mom-btn-primary" onClick={() => setEnrollModalOpen(true)} style={{ padding: '12px 28px', fontSize: 14 }}>
                                                Enroll Again
                                            </button>
                                        </div>
                                    )}
                                    {purchasableEnrollable.length === 0 && waitlistEnrollable.length > 0 && (
                                        <div>
                                            {waitlistEnrollable.map((pod) => {
                                                const isOnWaitlist = userWaitlistPod?.id === pod.id;
                                                return (
                                                    <div key={pod.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--mom-cream)', borderRadius: 12, gap: 12 }}>
                                                        <div>
                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{pod.podName}</p>
                                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>Starts {new Date(pod.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                                                        </div>
                                                        {isOnWaitlist ? (
                                                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>On waitlist ✓</span>
                                                        ) : (
                                                            <button className="mom-btn" onClick={() => { joinWaitlist(pod.id, userId, userEmail).then(() => setUserWaitlistPod(pod)); }}
                                                                style={{ padding: '8px 18px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none' }}>
                                                                Join Waitlist
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* 4. Testimonials */}
                        <TestimonialsSection testimonials={(siteContent?.testimonials || []).filter((t) => t.programIds && t.programIds.includes(program.id))} compact />

                        {/* 4. FAQs — collapsed */}
                        <FAQSection faqs={program.faqs} defaultOpen={false} />

                        {/* 5. What's Included — collapsed */}
                        {program.highlights && program.highlights.length > 0 && (
                            <CollapsibleSection title="What's Included" defaultOpen={false}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {program.highlights.map((h, i) => (
                                        <div key={i} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', padding: '10px 16px', background: 'var(--mom-cream)', borderRadius: 12 }}>
                                            <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>✓</span>{h}
                                        </div>
                                    ))}
                                </div>
                            </CollapsibleSection>
                        )}

                        {/* 6. Program Details — collapsed */}
                        {program.details && (
                            <CollapsibleSection title="Program Details" defaultOpen={false}>
                                <FormattedText text={program.details} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)' }} />
                            </CollapsibleSection>
                        )}

                        {/* 7. About Danielle */}
                        <AboutDanielleTeaser siteContent={siteContent} />
                    </>
                ) : (
                    /* ═══ NOT PURCHASED VIEW ═══ */
                    <div>
                        {/* 1. Program Details */}
                        {program.details && (
                            <CollapsibleSection title="Program Details" defaultOpen={true}>
                                <FormattedText text={program.details} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)' }} />
                            </CollapsibleSection>
                        )}

                        {/* 2. Testimonials */}
                        <TestimonialsSection testimonials={(siteContent?.testimonials || []).filter((t) => t.programIds && t.programIds.includes(program.id))} />

                        {/* 3. FAQs — collapsed */}
                        <FAQSection faqs={program.faqs} defaultOpen={false} />

                        {/* 4. What's Included */}
                        {program.highlights && program.highlights.length > 0 && (
                            <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 20, border: '1px solid rgba(255,255,255,0.55)', padding: '28px 32px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>What's Included</h3>
                                {program.highlights.map((h, i) => (
                                    <div key={i} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-charcoal)', padding: '8px 0', borderBottom: '1px solid var(--mom-cream-dark)' }}>
                                        <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>✓</span>{h}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 5. Upcoming Groups (cohort only) */}
                        {isCohort && availablePods.length > 0 && (
                            <div style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', padding: '32px', marginBottom: 24, boxShadow: 'var(--shadow-md)' }}>
                                <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>Upcoming Groups</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {availablePods.map((pod) => {
                                        const spotsLeft = (pod.maxParticipants || 0) - (pod.participants?.length || 0);
                                        const saleStatus = getPodSaleStatus(pod);
                                        const isOnWaitlist = userWaitlistPod?.id === pod.id;
                                        // Derive meeting schedule summary
                                        const meetings = pod.meetings || [];
                                        const meetingCount = meetings.length;
                                        let scheduleHint = '';
                                        if (meetingCount > 0) {
                                            const days = meetings.filter((m) => m.date).map((m) => new Date(m.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }));
                                            const dayFreq = {};
                                            days.forEach((d) => { dayFreq[d] = (dayFreq[d] || 0) + 1; });
                                            const topDay = Object.entries(dayFreq).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                                            const times = meetings.filter((m) => m.time).map((m) => m.time);
                                            const timeFreq = {};
                                            times.forEach((t) => { timeFreq[t] = (timeFreq[t] || 0) + 1; });
                                            const topTime = Object.entries(timeFreq).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                                            // Render the top meeting time in the viewer's local tz by picking any
                                            // meeting that uses it and converting its trainer-local wall clock to UTC.
                                            const sampleMeeting = topTime ? meetings.find((m) => m.time === topTime && m.date) : null;
                                            const sampleMoment = sampleMeeting ? trainerWallClockToUTC(sampleMeeting.date, sampleMeeting.time) : null;
                                            const timeFormatted = sampleMoment ? formatViewerLocalTime(sampleMoment) : '';
                                            scheduleHint = `${meetingCount} weekly call${meetingCount !== 1 ? 's' : ''}`;
                                            if (topDay && timeFormatted) scheduleHint += ` · ${topDay}s at ${timeFormatted}`;
                                            else if (topDay) scheduleHint += ` · ${topDay}s`;
                                        }
                                        return (
                                            <div key={pod.id} style={{ padding: '18px 22px', borderRadius: 14, border: isOnWaitlist ? '1.5px solid var(--mom-primary)' : '1.5px solid var(--mom-cream-dark)', background: isOnWaitlist ? 'color-mix(in srgb, var(--mom-primary) 6%, transparent)' : 'var(--mom-white)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{pod.podName}</p>
                                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginTop: 4 }}>Starts {new Date(pod.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                                                        {scheduleHint && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginTop: 4 }}>📅 {scheduleHint}</p>}
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        {(saleStatus === 'immediate' || saleStatus === 'on-sale') && spotsLeft <= 3 && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-primary)' }}>{spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left</p>}
                                                    </div>
                                                </div>
                                                {saleStatus === 'waitlist' && (
                                                    <div style={{ marginTop: 12 }}>
                                                        {isOnWaitlist ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>You're on the waitlist ✓</span>
                                                                <button className="mom-btn mom-btn-ghost" onClick={() => { leaveWaitlist(pod.id, userId).then(() => { setUserWaitlistPod(null); }); }} style={{ fontSize: 12, color: 'var(--mom-warm-gray)' }}>Leave waitlist</button>
                                                            </div>
                                                        ) : (
                                                            <button className="mom-btn" onClick={() => { setJoiningWaitlist(true); joinWaitlist(pod.id, userId, userEmail).then(() => { setUserWaitlistPod(pod); setJoiningWaitlist(false); }); }} disabled={joiningWaitlist} style={{ padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none', opacity: joiningWaitlist ? 0.5 : 1 }}>
                                                                {joiningWaitlist ? 'Joining...' : 'Join Waitlist'}
                                                            </button>
                                                        )}
                                                        {pod.saleStartDate && !isOnWaitlist && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 6 }}>Enrollment opens {new Date(pod.saleStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {!userId && !demoMode && availablePods.some((p) => getPodSaleStatus(p) === 'waitlist') && (
                                    <AlreadyEnrolledLogin siteContent={siteContent} onLogIn={handleAlreadyEnrolledLogin} marginTop={16} />
                                )}
                            </div>
                        )}

                        {/* 6. About Danielle */}
                        <AboutDanielleTeaser siteContent={siteContent} />

                        {/* Purchase CTA */}
                        {(() => {
                            const purchasablePods = availablePods.filter((p) => { const s = getPodSaleStatus(p); return s === 'immediate' || s === 'on-sale'; });
                            const waitlistPods = availablePods.filter((p) => getPodSaleStatus(p) === 'waitlist');
                            const hasPurchasable = purchasablePods.length > 0;
                            const hasWaitlist = waitlistPods.length > 0;
                            const onlyWaitlist = isCohort && hasWaitlist && !hasPurchasable;
                            const noPods = isCohort && availablePods.length === 0;
                            if (noPods) {
                                return (
                                    <div className="mom-cta-card" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', padding: '40px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
                                        {onProgramWaitlist ? (
                                            <>
                                                <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>You're on the waitlist!</p>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7, marginBottom: 16 }}>We'll let you know when the next round of {program.name} opens up.</p>
                                                <button className="mom-btn mom-btn-ghost" onClick={async () => { await leaveProgramWaitlist(userId, program.id); setOnProgramWaitlist(false); }} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Leave waitlist</button>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>Interested in this program?</p>
                                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7, marginBottom: 16 }}>No groups are currently open. Join the waitlist and we'll notify you when enrollment starts.</p>
                                                <div className={!userId && !demoMode ? 'mom-waitlist-actions mom-waitlist-actions--center' : undefined}>
                                                <button className="mom-btn" onClick={async () => { if (!userId) { clerk.openSignIn(); return; } setJoiningProgramWaitlist(true); await joinProgramWaitlist(userId, userEmail, program.id, program.name); setOnProgramWaitlist(true); setJoiningProgramWaitlist(false); }} disabled={joiningProgramWaitlist} style={{ padding: '14px 40px', borderRadius: 14, fontSize: 15, fontWeight: 600, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', border: 'none', opacity: joiningProgramWaitlist ? 0.5 : 1 }}>
                                                    {joiningProgramWaitlist ? 'Joining...' : 'Join Waitlist'}
                                                </button>
                                                {!userId && !demoMode && <AlreadyEnrolledLogin variant="button" padY={14} padX={40} siteContent={siteContent} onLogIn={handleAlreadyEnrolledLogin} />}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            }
                            if (onlyWaitlist && userWaitlistPod) { const saleDate = userWaitlistPod.saleStartDate ? new Date(userWaitlistPod.saleStartDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : null; return (<div className="mom-cta-card" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', padding: '40px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}><p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>You're on the list!</p><p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7 }}>{saleDate ? `Enrollment opens ${saleDate}. We'll keep you posted!` : "We'll let you know when enrollment opens. Keep an eye on your email!"}</p></div>); }
                            if (onlyWaitlist && !userWaitlistPod) return null;
                            return (
                                <div className="mom-cta-card" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', padding: '40px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
                                    {checkingPayment ? (<><div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} /><p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>Confirming your purchase...</p><p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)' }}>This usually takes just a moment.</p></>) : (<><p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8 }}>Ready to get started?</p><p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 32, fontWeight: 700, color: 'var(--mom-primary)', marginBottom: 16 }}>{`$${program.price} USD`}</p>{isCohort ? (<button className="mom-btn mom-btn-primary" onClick={() => setEnrollModalOpen(true)} style={{ padding: '16px 44px', fontSize: 16 }}>Enroll Now</button>) : (<button className="mom-btn mom-btn-primary" onClick={() => handlePurchase()} disabled={purchaseProcessing} style={{ padding: '16px 44px', fontSize: 16, opacity: purchaseProcessing ? 0.5 : 1 }}>{purchaseProcessing ? 'Redirecting to checkout...' : 'Purchase Program'}</button>)}</>)}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
            {activeVideo && (
                <VideoPlayerModal
                    video={activeVideo}
                    onClose={() => setActiveVideo(null)}
                    userId={userId}
                    isAdmin={isAdmin}
                    programId={program.id}
                    onAutoComplete={handleModalAutoComplete}
                    completionCount={activeVideo?.id ? (perVideoCompletions[activeVideo.id]?.count || 0) : 0}
                    lastCompletedAt={activeVideo?.id ? (perVideoCompletions[activeVideo.id]?.lastCompletedAt || null) : null}
                    completionCopy={completionCopy}
                    onUndoComplete={handleUndoComplete}
                />
            )}

            {/* ── ENROLLMENT MODAL (Cohort group selection) ────────── */}
            {enrollModalOpen && isCohort && (() => {
                // For re-enrollment, only show pods user isn't already in
                const podsForModal = isPurchased ? enrollablePods : availablePods;
                const purchasable = podsForModal.filter((p) => { const s = getPodSaleStatus(p); return s === 'immediate' || s === 'on-sale'; });
                // Auto-select if only one purchasable group
                const effectiveSelected = purchasable.length === 1 ? purchasable[0].id : selectedPodId;
                return (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => { setEnrollModalOpen(false); setPurchaseProcessing(false); }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }} />
                        <div style={{ position: 'relative', background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)', padding: '36px 32px 32px', maxWidth: 480, width: '100%', boxShadow: 'var(--shadow-lg)', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => { setEnrollModalOpen(false); setPurchaseProcessing(false); }} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', fontSize: 22, color: 'var(--mom-warm-gray)', cursor: 'pointer', lineHeight: 1 }} aria-label="Close">×</button>
                            <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6 }}>Choose Your Group</h3>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 20 }}>Select which group you'd like to join for <strong>{program.name}</strong></p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                                {purchasable.map((pod) => {
                                    const spotsLeft = (pod.maxParticipants || 0) - (pod.participants?.length || 0);
                                    const isSelected = effectiveSelected === pod.id;
                                    const mtgs = pod.meetings || [];
                                    let hint = '';
                                    if (mtgs.length > 0) {
                                        const days = mtgs.filter((m) => m.date).map((m) => new Date(m.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }));
                                        const df = {}; days.forEach((d) => { df[d] = (df[d] || 0) + 1; }); const topDay = Object.entries(df).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                                        const times = mtgs.filter((m) => m.time).map((m) => m.time);
                                        const tf = {}; times.forEach((t) => { tf[t] = (tf[t] || 0) + 1; }); const topTime = Object.entries(tf).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                                        // Render top time in viewer-local tz (convert the actual moment, not the bare wall clock).
                                        const sampleMeeting = topTime ? mtgs.find((m) => m.time === topTime && m.date) : null;
                                        const sampleMoment = sampleMeeting ? trainerWallClockToUTC(sampleMeeting.date, sampleMeeting.time) : null;
                                        const timeFmt = sampleMoment ? formatViewerLocalTime(sampleMoment) : '';
                                        hint = `${mtgs.length} call${mtgs.length !== 1 ? 's' : ''}`;
                                        if (topDay && timeFmt) hint += ` · ${topDay}s at ${timeFmt}`;
                                        else if (topDay) hint += ` · ${topDay}s`;
                                    }
                                    return (
                                        <div key={pod.id} onClick={() => setSelectedPodId(pod.id)}
                                            style={{ padding: '16px 20px', borderRadius: 14, border: isSelected ? '2px solid var(--mom-primary)' : '1.5px solid var(--mom-cream-dark)', background: isSelected ? 'color-mix(in srgb, var(--mom-primary) 4%, transparent)' : 'var(--mom-white)', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{pod.podName}</p>
                                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginTop: 3 }}>
                                                        Starts {new Date(pod.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                                    </p>
                                                    {hint && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', marginTop: 3 }}>📅 {hint}</p>}
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {spotsLeft <= 3 && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>{spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left</p>}
                                                    {isSelected && <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-primary)', fontWeight: 600 }}>Selected ✓</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button className="mom-btn mom-btn-primary" disabled={!effectiveSelected || purchaseProcessing} onClick={() => {
                                handlePurchase(effectiveSelected);
                            }} style={{ width: '100%', padding: '14px 24px', fontSize: 15, opacity: (!effectiveSelected || purchaseProcessing) ? 0.5 : 1 }}>
                                {purchaseProcessing ? 'Redirecting to checkout...' : 'Continue to Checkout →'}
                            </button>
                            {!userId && !demoMode && (<p style={{ textAlign: 'center', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, lineHeight: 1.5, color: 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))', margin: '12px 0 0' }}>{siteContent?.programSignInFirstNote || "You'll create your free account (or sign in) first — then pay. It keeps your purchase safely tied to you."}</p>)}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

export default ProgramView;